import type { CdkTree, CdkTreeNode } from '@angular/cdk/tree';
import { computed, effect, EventEmitter, Signal, signal, TrackByFunction, WritableSignal } from '@angular/core';
import gsap from 'gsap';
import { PrivilegeNode } from '@artemis-software/wr-api';
import { pluralize } from '@/utils/pluralize';
import PrivilegeEnum = PrivilegeNode.PrivilegeEnum;

export type PrivilegeTreeNode = PrivilegeNode & {
  children?: PrivilegeTreeNode[];
};

export class PrivilegeHierarchyModel {
  constructor(
    readonly tree: Signal<CdkTree<PrivilegeTreeNode>>,
    readonly treeNodes: Signal<readonly CdkTreeNode<PrivilegeTreeNode>[]>,
    readonly rootNodes: Signal<PrivilegeTreeNode[]>,
    readonly selection: WritableSignal<PrivilegeEnum[]>,
    readonly allowedPrivileges: Signal<PrivilegeEnum[]>,
  ) {
    this.selectedPrivileges = new Set<PrivilegeNode.PrivilegeEnum>();

    effect(() =>
      this.selectedPrivileges = new Set(selection()),
    );
  }

  private selectedPrivileges: Set<PrivilegeEnum>;

  private readonly nodeIdMap = new NodeIdMap();

  readonly expansionKey = (node: PrivilegeTreeNode) => this.nodeIdMap.getId(node);

  readonly isExpandable = (node: PrivilegeTreeNode) => 'children' in node;

  readonly trackBy: TrackByFunction<any> = (index, node: PrivilegeTreeNode) => this.nodeIdMap.getId(node);

  readonly childrenAccessor = (node: PrivilegeTreeNode): PrivilegeTreeNode[] => node?.children ?? [];

  readonly valueChanged = new EventEmitter();

  searchTerm = signal('');

  private readonly actualSearchTerm = computed(() => this.searchTerm().trim().toLowerCase());

  isVisible = (node: PrivilegeTreeNode, ignoreChildren = false): boolean => {
    const term = this.actualSearchTerm();

    if (term.length === 0)
      return true;

    const parent = this.findParent(node);

    if (parent && this.isVisible(parent, true))
      return true;

    if (!ignoreChildren && node.children?.some(child => this.isVisible(child, false)))
      return true;

    return node.name.toLowerCase().includes(term) || !!node.description?.toLowerCase().includes(term);
  };

  updateSearchTerm(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.set(value);
  }

  private findParent(node: PrivilegeTreeNode): PrivilegeTreeNode | undefined {
    return this.findNodes(it => it.children?.includes(node) ?? false)[0];
  }

  isSelected(node: PrivilegeTreeNode): boolean {
    if (node.children)
      return node.children.every(child => this.isSelected(child));

    if (node.privilege)
      return this.selectedPrivileges.has(node.privilege);

    return false;
  }

  isIndeterminate(node: PrivilegeTreeNode) {
    if (!node.children)
      return false;

    return node.children.some(child => this.isSelected(child)) && !node.children.every(child => this.isSelected(child));
  }

  select(node: PrivilegeTreeNode) {
    if (this.isDisabled(node))
      return;

    if (!this.ensureNodeCanBeSelected(node))
      return;

    if (node.privilege)
      this.selectedPrivileges.add(node.privilege);
    if (node.children)
      node.children.forEach(child => this.select(child));
    this.updateSelection();
  }

  deselect(node: PrivilegeTreeNode) {
    if (this.isDisabled(node))
      return;

    if (!this.ensureNodeCanBeDeselected(node))
      return;

    if (node.privilege)
      this.selectedPrivileges.delete(node.privilege);

    if (node.children)
      node.children.forEach(child => this.deselect(child));

    this.updateSelection();
  }

  setSelected(node: PrivilegeTreeNode, selected: boolean) {
    if (selected)
      this.select(node);
    else
      this.deselect(node);
  }

  toggleSelected(node: PrivilegeTreeNode) {
    if (this.isSelected(node))
      this.deselect(node);
    else
      this.select(node);
  }

  canBeSelected(node: PrivilegeTreeNode) {
    const dependencies = this.getNodeDependencies(node);

    if (dependencies.length === 0)
      return true;

    return dependencies.every(dependency => this.isSelected(dependency));
  }

  canBeDeselected(node: PrivilegeTreeNode) {
    const dependents = this.getNodeDependents(node);

    if (dependents.length === 0)
      return true;

    return dependents.every(dependent => !this.isSelected(dependent));
  }

  private getTreeNode(node: PrivilegeTreeNode): CdkTreeNode<PrivilegeTreeNode> | undefined {
    return this.treeNodes().find(it => it.data === node);
  }

  private getNodeElement(node: PrivilegeTreeNode): HTMLElement | undefined {
    //element ref is a protected property
    return (this.getTreeNode(node) as any)?._elementRef.nativeElement;
  }

  private getPathToNode(node: PrivilegeTreeNode): PrivilegeTreeNode[] {
    function findPathRecursive(current: PrivilegeTreeNode, path: PrivilegeTreeNode[]): PrivilegeTreeNode[] | null {
      if (current === node)
        return path;

      if (current.children) {
        for (const child of current.children) {
          const childPath = findPathRecursive(child, [...path, current]);
          if (childPath)
            return childPath;
        }
      }

      return null;
    }

    for (const rootNode of this.rootNodes()) {
      const path = findPathRecursive(rootNode, []);
      if (path)
        return path;
    }

    console.warn('No path found for node', node);
    return [];
  }

  //TODO: needs an angular cdk 18
  /*private ensureNodeIsVisible(node: PrivilegeTreeNode) {
    const path = this.getPathToNode(node);
    path.forEach(it => this.getTreeNode(it)?.expand());
  }*/

  private shake(node: PrivilegeTreeNode) {
    const el = this.getNodeElement(node);
    if (!el)
      return;

    //this.ensureNodeIsVisible(node);

    gsap.timeline()
      .to(el, {
        x: -6,
        duration: 0.075,
      })
      .to(el, {
        x: 4,
        duration: 0.075,
      })
      .to(el, {
        x: -2,
        duration: 0.075,
      })
      .to(el, {
        x: 0,
        duration: 0.075,
      });
  }

  ensureNodeCanBeSelected(node: PrivilegeTreeNode): boolean {
    const dependencies = this.getNodeDependencies(node)
      .filter(it => !this.isSelected(it));

    if (dependencies.some(it => this.isDisabled(it))) {
      this.shake(node);
      return false;
    }

    if (dependencies.length > 0) {
      const privilegeWord = pluralize(dependencies.length, 'Berechtigung', 'Berechtigungen');

      //TODO: add dialog
      /*this.confirmationService.confirm({
        target: this.getNodeElement(node),
        key: 'select',
        data: {
          type: 'select',
          name: node.name,
          dependencies: dependencies.map(it => it.name),
        },
        accept: () => {

          this.select(node);
          this.updateSelection();
        },
        acceptLabel: `${privilegeWord} aktivieren`,
        rejectLabel: 'Abbrechen',
        rejectButtonProps: {
          severity: 'secondary',
        },
      } as Confirmation);*/

      for (const dependency of dependencies) {
        if (dependency.privilege)
          this.selectedPrivileges.add(dependency.privilege);
      }

      return true;
    }

    return true;
  }

  ensureNodeCanBeDeselected(node: PrivilegeTreeNode): boolean {
    const dependants = this.getNodeDependents(node)
      .filter(it => this.isSelected(it));

    if (dependants.some(it => this.isDisabled(it))) {
      this.shake(node);
      return false;
    }

    for (const dependant of dependants) {
      if (dependant.privilege)
        this.selectedPrivileges.delete(dependant.privilege);
    }

    return true;
  }

  private isChildOf(parent: PrivilegeTreeNode, node: PrivilegeTreeNode): boolean {
    if (parent.children) {
      if (parent.children.includes(node))
        return true;

      for (const child of parent.children) {
        if (this.isChildOf(child, node))
          return true;
      }
    }

    return false;
  }

  private findNodes(selector: (node: PrivilegeTreeNode) => boolean): PrivilegeTreeNode[] {
    const result: PrivilegeTreeNode[] = [];

    const collectNodes = (nodes: PrivilegeTreeNode[]) => {
      for (const node of nodes) {
        if (selector(node))
          result.push(node);
        if (node.children)
          collectNodes(node.children);
      }
    };

    collectNodes(this.rootNodes());

    return result;
  }

  private getNodeDependencies(node: PrivilegeTreeNode): PrivilegeTreeNode[] {
    const nodes = new Set<PrivilegeTreeNode>();

    const collectDependenciesRecursive = (current: PrivilegeTreeNode) => {
      if (nodes.has(current))
        return;

      if (current.children) {
        current.children.forEach(child => collectDependenciesRecursive(child));
        return;
      }

      if (current.dependencies) {
        for (const privilege of current.dependencies) {
          const dependentNode = this.findNodeForPrivilege(privilege);
          if (dependentNode) {
            collectDependenciesRecursive(dependentNode);

            nodes.add(dependentNode);
          }
        }
      }
    };

    collectDependenciesRecursive(node);

    return [...nodes].filter(it => !this.isChildOf(node, it));
  }

  private getNodeDependents(node: PrivilegeTreeNode) {
    const result = new Set<PrivilegeTreeNode>();
    const visited = new Set<PrivilegeTreeNode>();

    const collectDependentsRecursive = (current: PrivilegeTreeNode) => {
      if (visited.has(current))
        return;

      visited.add(current);

      if (current.privilege) {
        const directDependents = this.findNodes(it => it.dependencies?.includes(current.privilege));

        directDependents.forEach(it => {
          result.add(it);
          collectDependentsRecursive(it);
        });
      }

      if (current.children) {
        for (const child of current.children)
          collectDependentsRecursive(child);
      }
    };

    collectDependentsRecursive(node);

    return [...result].filter(it => !this.isChildOf(node, it));
  }

  private findNodeForPrivilege(
    privilege: PrivilegeNode.DependenciesEnum,
  ): PrivilegeTreeNode | undefined {
    const nodes = this.findNodes(it => it.privilege === privilege);

    return nodes[0];
  }

  private updateSelection() {
    this.selection.set([...this.selectedPrivileges]);
    this.valueChanged.emit();
  }

  isDisabled(node: PrivilegeTreeNode): boolean {
    if (node.privilege)
      return !this.allowedPrivileges().includes(node.privilege);
    if (node.children)
      return node.children.every(child => this.isDisabled(child));

    return false;
  }
}

class NodeIdMap {
  private readonly ids = new WeakMap<PrivilegeTreeNode, number>();

  private nextId = 0;

  getId(node: PrivilegeTreeNode) {
    const id = this.ids.get(node);
    if (id !== undefined)
      return id;

    const newId = this.nextId++;
    this.ids.set(node, newId);
    return newId;
  }
}
